// Pure pairing helpers for the Generate Bracket dialog. Kept free of React and
// MUI imports so the pairing rules can be exercised on their own.

let pairSequence = 0;

export const newPair = (section1Id, section2Id) => {
  pairSequence += 1;
  return { id: `pair-${pairSequence}`, section1Id, section2Id };
};

export const pairInOrder = (sections) => {
  const pairs = [];
  for (let index = 0; index + 1 < sections.length; index += 2) {
    pairs.push(newPair(sections[index].id, sections[index + 1].id));
  }
  return pairs;
};

// Start from the pairings the sections already carry (winner plays section),
// then pair whatever is left in section order so the step opens complete.
export const buildSectionMatches = (sections) => {
  const used = new Set();
  const pairs = [];
  const byId = new Map(sections.map((section) => [section.id, section]));

  sections.forEach((section) => {
    if (used.has(section.id) || !section.winnerPlaysSection) {
      return;
    }

    const opponent = byId.get(section.winnerPlaysSection);
    if (!opponent || opponent.id === section.id || used.has(opponent.id)) {
      return;
    }

    pairs.push(newPair(section.id, opponent.id));
    used.add(section.id);
    used.add(opponent.id);
  });

  const remaining = sections.filter((section) => !used.has(section.id));
  for (let index = 0; index + 1 < remaining.length; index += 2) {
    pairs.push(newPair(remaining[index].id, remaining[index + 1].id));
  }

  return pairs;
};

export const shuffle = (list) => {
  const copy = [...list];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[other]] = [copy[other], copy[index]];
  }
  return copy;
};

// ---------------------------------------------------------------- play-offs
// The play-off rows are the first `playoffCount` pairings in draw order; every
// section not in one of them goes straight into the stage round.
export const buildPlayoffMatches = (sections, playoffCount) =>
  buildSectionMatches(sections).slice(0, playoffCount);

export const drawPlayoffMatches = (sections, playoffCount) =>
  pairInOrder(shuffle(sections).slice(0, playoffCount * 2));

// Keep the pairings already chosen and fill empty sides from the sections that
// are not used yet, in section order.
export const fillPlayoffMatches = (sections, matches, playoffCount) => {
  const used = new Set();
  matches.forEach((pair) => {
    if (pair.section1Id) {
      used.add(pair.section1Id);
    }
    if (pair.section2Id) {
      used.add(pair.section2Id);
    }
  });
  const remaining = sections.filter((section) => !used.has(section.id));
  let cursor = 0;

  const filled = matches.map((pair) => {
    const next = { ...pair };
    if (!next.section1Id && cursor < remaining.length) {
      next.section1Id = remaining[cursor].id;
      cursor += 1;
    }
    if (!next.section2Id && cursor < remaining.length) {
      next.section2Id = remaining[cursor].id;
      cursor += 1;
    }
    return next;
  });

  while (filled.length < playoffCount && cursor + 1 < remaining.length) {
    filled.push(newPair(remaining[cursor].id, remaining[cursor + 1].id));
    cursor += 2;
  }

  return filled;
};

// Build the resolved `rounds` tree the bracket service accepts. Play-off
// winners are spread across the stage round's sides, so they do not meet each
// other until later rounds while there are enough sides to separate them; the
// direct sections fill the remaining places.
export const buildPlayoffRounds = (sectionType, sections, playoffMatches) => {
  const used = new Set(
    playoffMatches.flatMap((pair) => [pair.section1Id, pair.section2Id])
  );
  const direct = sections.filter((section) => !used.has(section.id));
  const stageSides = sectionType.stage;
  const slots = new Array(stageSides).fill(null);
  const winnerSlots = new Set(
    playoffMatches.map((_, index) =>
      Math.floor((index * stageSides) / playoffMatches.length)
    )
  );

  let winnerIndex = 0;
  let directIndex = 0;
  for (let slot = 0; slot < stageSides; slot += 1) {
    if (winnerSlots.has(slot)) {
      slots[slot] = { kind: 'winnerOf', roundIndex: 0, matchIndex: winnerIndex };
      winnerIndex += 1;
    } else {
      const section = direct[directIndex];
      directIndex += 1;
      slots[slot] = section
        ? { kind: 'section', id: section.id, name: section.name }
        : { kind: 'tbd' };
    }
  }

  const rounds = [
    {
      name: 'Play-offs',
      matches: playoffMatches.map((pair, index) => ({
        name: `Play-off ${index + 1}`,
        sides: [
          { kind: 'section', id: pair.section1Id },
          { kind: 'section', id: pair.section2Id }
        ]
      }))
    },
    {
      name: sectionType.stageLabel,
      matches: Array.from({ length: stageSides / 2 }, (_, matchIndex) => ({
        name: `${sectionType.stageLabel} - Match ${matchIndex + 1}`,
        sides: [slots[matchIndex * 2], slots[matchIndex * 2 + 1]]
      }))
    }
  ];

  sectionType.rounds.slice(2).forEach((roundPlan) => {
    const previousRound = rounds.length - 1;
    rounds.push({
      name: roundPlan.name,
      matches: Array.from({ length: roundPlan.matches }, (_, matchIndex) => ({
        name:
          roundPlan.matches === 1
            ? 'Final'
            : `${roundPlan.name} - Match ${matchIndex + 1}`,
        sides: [
          { kind: 'winnerOf', roundIndex: previousRound, matchIndex: matchIndex * 2 },
          {
            kind: 'winnerOf',
            roundIndex: previousRound,
            matchIndex: matchIndex * 2 + 1
          }
        ]
      }))
    });
  });

  return rounds;
};

export const summarizeSectionPairings = (sections, matches) => {
  const usedIds = matches
    .flatMap((pair) => [pair.section1Id, pair.section2Id])
    .filter(Boolean);
  const usedSet = new Set(usedIds);
  const duplicateIds = usedIds.length !== usedSet.size;
  const completePairs = matches.filter((pair) => pair.section1Id && pair.section2Id).length;
  const unpaired = sections.filter((section) => !usedSet.has(section.id));

  return { usedIds, usedSet, duplicateIds, completePairs, unpaired };
};

export const summarizeTeamRows = (teams, rows) => {
  const assigned = rows
    .flatMap((row) => [row.team1Id, row.team2Id])
    .filter((teamId) => teamId && teamId !== 'walkOver');
  const assignedSet = new Set(assigned);

  return {
    assignedSet,
    duplicateTeams: assigned.length !== assignedSet.size,
    placedTeams: assignedSet.size,
    unplacedTeams: Math.max(0, teams.length - assignedSet.size),
    filledMatches: rows.filter((row) => row.team1Id || row.team2Id).length,
    walkovers: rows.filter((row) => row.team2Id === 'walkOver').length
  };
};

export const totalMatches = (rounds) =>
  rounds.reduce((total, round) => total + round.matches, 0);

export const matchesLabel = (rounds) => `${totalMatches(rounds)} matches`;

export const roundsLabel = (rounds) =>
  `${rounds.length} rounds · ${totalMatches(rounds)} matches`;
